import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Star, Upload, ArrowUp, ArrowDown, Sparkles } from "lucide-react";

const CATEGORIES = [
  { key: "particulier", label: "Particulier" },
  { key: "sinistre", label: "Sinistre" },
  { key: "professionnel", label: "Professionnel" },
];

const EMPTY = {
  slug: "",
  titre: "",
  sous_titre: "",
  categorie: "particulier",
  annee: new Date().getFullYear(),
  chapeau: "",
  contenu: "",
  materiaux: [],
  photo_couverture: "",
  photos: [],
  avant_apres: [],
  est_phare: false,
  publiee: true,
  coordination_expert: false,
};

const slugify = (s) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export default function AdminRealisations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, _setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const setForm = (updater) => {
    setDirty(true);
    _setForm(updater);
  };
  const coverInput = useRef();
  const photoInput = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/realisations");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    _setForm(EMPTY);
    setEditing(null);
    setDirty(false);
    setDrawer(true);
  };

  const openEdit = (r) => {
    _setForm({ ...EMPTY, ...r });
    setEditing(r.id);
    setDirty(false);
    setDrawer(true);
  };

  const upload = async (file) => {
    const data = new FormData();
    data.append("file", file);
    const { data: res } = await api.post("/admin/upload", data);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    return `${BACKEND_URL}${res.url}`;
  };

  const onCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await upload(file);
      setForm((s) => ({ ...s, photo_couverture: url }));
      setDirty(true);
      toast.success("Photo de couverture importée — n'oublie pas d'enregistrer");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'import");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onPhotoFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const url = await upload(f);
        setForm((s) => ({ ...s, photos: [...s.photos, { url, legende: "" }] }));
      }
      setDirty(true);
      toast.success(`${files.length} photo(s) importée(s) — n'oublie pas d'enregistrer`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'import");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onAvantApresFile = async (e, index, slot) => {
    // slot = "avant_url" | "apres_url"
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await upload(file);
      setForm((s) => {
        const pairs = [...(s.avant_apres || [])];
        pairs[index] = { ...(pairs[index] || { avant_url: "", apres_url: "", legende: "" }), [slot]: url };
        return { ...s, avant_apres: pairs };
      });
      setDirty(true);
      toast.success("Photo importée — n'oublie pas d'enregistrer");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'import");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addAvantApresPair = () =>
    setForm((s) => ({ ...s, avant_apres: [...(s.avant_apres || []), { avant_url: "", apres_url: "", legende: "" }] }));

  const removeAvantApresPair = (i) =>
    setForm((s) => ({ ...s, avant_apres: (s.avant_apres || []).filter((_, idx) => idx !== i) }));

  const setAvantApresLegende = (i, legende) =>
    setForm((s) => {
      const pairs = [...(s.avant_apres || [])];
      pairs[i] = { ...pairs[i], legende };
      return { ...s, avant_apres: pairs };
    });

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      slug: form.slug || slugify(form.titre),
      annee: parseInt(form.annee, 10) || new Date().getFullYear(),
      materiaux: Array.isArray(form.materiaux)
        ? form.materiaux
        : String(form.materiaux || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
    };
    try {
      if (editing) {
        await api.patch(`/admin/realisations/${editing}`, payload);
        toast.success("Réalisation mise à jour");
      } else {
        await api.post("/admin/realisations", payload);
        toast.success("Réalisation créée");
      }
      setDirty(false);
      setDrawer(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cette réalisation ?")) return;
    try {
      await api.delete(`/admin/realisations/${id}`);
      setItems((s) => s.filter((r) => r.id !== id));
      toast.success("Réalisation supprimée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const togglePhare = async (r) => {
    try {
      const { data } = await api.patch(`/admin/realisations/${r.id}`, { est_phare: !r.est_phare });
      setItems((s) => s.map((x) => (x.id === r.id ? data : x)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    // Reassign ordre based on new positions (10, 20, 30...) — leaves room for future insertions
    const reorderPayload = next.map((r, i) => ({ id: r.id, ordre: (i + 1) * 10 }));
    const optimistic = next.map((r, i) => ({ ...r, ordre: (i + 1) * 10 }));
    setItems(optimistic);
    try {
      await api.post("/admin/realisations/reorder", reorderPayload);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Réordonnancement échoué");
      // rollback
      setItems(items);
    }
  };

  const [suggesting, setSuggesting] = useState(false);
  const [aiTitreSuggestion, setAiTitreSuggestion] = useState("");
  const suggestAI = async () => {
    if (!form.photo_couverture) {
      toast.error("Importez d'abord une photo de couverture — l'IA en a besoin.");
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await api.post("/admin/realisations/suggest", {
        photo_url: form.photo_couverture,
        titre: form.titre,
        categorie: form.categorie,
        annee: Number(form.annee) || undefined,
        materiaux: form.materiaux || [],
        coordination_expert: !!form.coordination_expert,
      });
      // If user has no title yet, apply AI title directly and auto-generate slug
      const applyTitreNow = !form.titre && !!data.titre;
      setForm((prev) => ({
        ...prev,
        titre: applyTitreNow ? data.titre : prev.titre,
        slug: applyTitreNow ? slugify(data.titre) : prev.slug,
        chapeau: data.chapeau || prev.chapeau,
        contenu: data.contenu || prev.contenu,
        sous_titre: prev.sous_titre || data.sous_titre || "",
      }));
      // If user has a title AND AI returned a different one, show it as a suggestion to apply
      if (form.titre && data.titre && data.titre.toLowerCase() !== form.titre.toLowerCase()) {
        setAiTitreSuggestion(data.titre);
      } else {
        setAiTitreSuggestion("");
      }
      toast.success("Suggestion appliquée — ajustez le ton si besoin.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de la suggestion IA");
    } finally {
      setSuggesting(false);
    }
  };
  const applyAiTitre = () => {
    if (!aiTitreSuggestion) return;
    setForm((prev) => ({
      ...prev,
      titre: aiTitreSuggestion,
      slug: slugify(aiTitreSuggestion),
    }));
    setAiTitreSuggestion("");
    toast.success("Titre SEO appliqué.");
  };

  return (
    <div data-testid="admin-realisations-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-eyebrow mb-3">Réalisations</p>
          <h1 className="h-display text-4xl md:text-5xl">Vitrine du site.</h1>
          <p className="text-[13px] text-muted-foreground mt-3 max-w-2xl">
            L'ordre d'affichage sur le site public suit cette liste. Utilisez les flèches sur chaque vignette pour réorganiser. Les projets phares (★) apparaissent toujours en premier.
          </p>
        </div>
        <button onClick={openNew} className="btn-terra" data-testid="realisation-new">
          <Plus size={14} /> Nouvelle réalisation
        </button>
      </div>

      {loading ? (
        <p className="text-serial">Chargement</p>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-[14px]">Aucune réalisation pour le moment.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="realisations-grid">
          {items.map((r, idx) => (
            <div key={r.id} className="border border-border" data-testid={`realisation-card-${r.id}`}>
              <div className="aspect-[4/3] bg-muted overflow-hidden relative">
                {r.photo_couverture ? (
                  <img src={r.photo_couverture} alt={r.titre} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-[12px]">
                    Pas de photo
                  </div>
                )}
                {/* Order badge + reorder buttons */}
                <div className="absolute top-3 left-3 flex items-center gap-1">
                  <span className="bg-background/95 backdrop-blur-sm text-[10.5px] tracking-[0.18em] uppercase font-medium px-2 py-1 text-foreground/80">
                    #{idx + 1}
                  </span>
                </div>
                <div className="absolute top-3 right-3 flex flex-col gap-1">
                  <button
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    title="Monter"
                    className="bg-background/95 backdrop-blur-sm hover:bg-primary hover:text-background disabled:opacity-30 disabled:cursor-not-allowed p-1.5 transition-colors"
                    data-testid={`realisation-move-up-${r.id}`}
                  >
                    <ArrowUp size={13} />
                  </button>
                  <button
                    onClick={() => move(idx, 1)}
                    disabled={idx === items.length - 1}
                    title="Descendre"
                    className="bg-background/95 backdrop-blur-sm hover:bg-primary hover:text-background disabled:opacity-30 disabled:cursor-not-allowed p-1.5 transition-colors"
                    data-testid={`realisation-move-down-${r.id}`}
                  >
                    <ArrowDown size={13} />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="text-serial">
                    {r.annee} · {r.categorie}
                    {!r.publiee && <span className="text-destructive ml-2">Brouillon</span>}
                  </p>
                  <button onClick={() => togglePhare(r)} className={r.est_phare ? "text-primary" : "text-muted-foreground/50 hover:text-primary"} title="Projet phare" data-testid={`realisation-phare-${r.id}`}>
                    <Star size={15} fill={r.est_phare ? "currentColor" : "none"} />
                  </button>
                </div>
                <p className="font-serif text-2xl leading-tight">{r.titre}</p>
                {r.sous_titre && <p className="text-[12.5px] text-muted-foreground mt-1">{r.sous_titre}</p>}
                <div className="flex gap-3 mt-4 text-[12px]">
                  <button onClick={() => openEdit(r)} className="text-eyebrow link-underline-static" data-testid={`realisation-edit-${r.id}`}>
                    Éditer
                  </button>
                  <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive ml-auto" data-testid={`realisation-delete-${r.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {drawer && (
        <div className="fixed inset-0 z-50 flex" data-testid="realisation-drawer">
          <div className="flex-1 bg-foreground/40" onClick={() => setDrawer(false)} />
          <form onSubmit={submit} className="w-full max-w-[640px] bg-background h-full overflow-y-auto p-8 border-l border-border">
            <div className="flex justify-between items-start mb-8">
              <div>
                <p className="text-eyebrow mb-2">{editing ? "Édition" : "Nouvelle"}</p>
                <h2 className="h-display text-3xl">Réalisation</h2>
              </div>
              <button type="button" onClick={() => setDrawer(false)} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-5">
              <Input label="Titre *" value={form.titre} onChange={(v) => setForm({ ...form, titre: v, slug: form.slug || slugify(v) })} required testId="real-titre" />
              {aiTitreSuggestion && (
                <div className="-mt-3 mb-1 p-3 bg-primary/5 border border-primary/30 flex items-start gap-3" data-testid="ai-titre-suggestion">
                  <Sparkles size={14} className="text-primary mt-0.5 shrink-0" />
                  <div className="flex-1 text-[12.5px]">
                    <p className="text-foreground/80 mb-2">
                      Titre optimisé SEO proposé :{" "}
                      <span className="font-medium text-foreground">« {aiTitreSuggestion} »</span>
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={applyAiTitre}
                        className="text-[11px] tracking-[0.16em] uppercase font-medium text-primary link-underline-static"
                        data-testid="ai-titre-apply"
                      >
                        Appliquer ce titre
                      </button>
                      <button
                        type="button"
                        onClick={() => setAiTitreSuggestion("")}
                        className="text-[11px] tracking-[0.16em] uppercase text-muted-foreground hover:text-foreground"
                        data-testid="ai-titre-dismiss"
                      >
                        Ignorer
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <Input label="Slug (URL)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="real-slug" />
              <Input label="Sous-titre" value={form.sous_titre} onChange={(v) => setForm({ ...form, sous_titre: v })} testId="real-sous-titre" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-eyebrow block mb-2">Catégorie</label>
                  <select
                    value={form.categorie}
                    onChange={(e) => setForm({ ...form, categorie: e.target.value })}
                    className="w-full bg-transparent border-b border-border py-2.5 text-[14.5px] outline-none"
                    data-testid="real-categorie"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <Input label="Année" type="number" value={form.annee} onChange={(v) => setForm({ ...form, annee: v })} testId="real-annee" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2 gap-3">
                  <label className="text-eyebrow">Chapeau *</label>
                  <button
                    type="button"
                    onClick={suggestAI}
                    disabled={suggesting || !form.photo_couverture}
                    className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase font-medium text-primary hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="real-ai-suggest"
                    title={!form.photo_couverture ? "Importez d'abord une photo de couverture" : "Générer titre, description et contenu à partir de la photo"}
                  >
                    <Sparkles size={13} />
                    {suggesting ? "L'IA rédige…" : "Suggérer avec l'IA"}
                  </button>
                </div>
                <textarea
                  required
                  value={form.chapeau}
                  onChange={(e) => setForm({ ...form, chapeau: e.target.value })}
                  placeholder="Une à deux phrases qui résument le dossier — visible sur la page projet."
                  className="w-full bg-transparent border border-border p-3 text-[14px] min-h-[80px]"
                  data-testid="real-chapeau"
                />
                {!form.photo_couverture && (
                  <p className="text-[11px] text-muted-foreground mt-1.5 italic">
                    Astuce : importez une photo de couverture plus bas, puis cliquez sur « Suggérer avec l'IA » — un chapeau et un contenu vous seront proposés en quelques secondes.
                  </p>
                )}
              </div>
              <div>
                <label className="text-eyebrow block mb-2">Contenu (paragraphes séparés par ligne vide)</label>
                <textarea
                  value={form.contenu}
                  onChange={(e) => setForm({ ...form, contenu: e.target.value })}
                  className="w-full bg-transparent border border-border p-3 text-[13.5px] min-h-[160px] font-serif"
                  data-testid="real-contenu"
                />
              </div>
              <Input
                label="Matériaux (séparés par virgules)"
                value={Array.isArray(form.materiaux) ? form.materiaux.join(", ") : form.materiaux}
                onChange={(v) => setForm({ ...form, materiaux: v.split(",").map((s) => s.trim()).filter(Boolean) })}
                testId="real-materiaux"
              />

              {/* Cover */}
              <div>
                <label className="text-eyebrow block mb-2">Photo de couverture</label>
                <div className="flex items-center gap-3">
                  <input value={form.photo_couverture || ""} onChange={(e) => setForm({ ...form, photo_couverture: e.target.value })} className="flex-1 bg-transparent border-b border-border py-2 text-[13px] outline-none" placeholder="URL ou téléverser" data-testid="real-cover-url" />
                  <input ref={coverInput} type="file" accept="image/*" className="hidden" onChange={onCoverFile} />
                  <button type="button" onClick={() => coverInput.current?.click()} className="text-eyebrow text-primary link-underline-static" disabled={uploading} data-testid="real-cover-upload">
                    <Upload size={12} className="inline mr-1" /> Importer une photo
                  </button>
                </div>
                {form.photo_couverture && (
                  <div className="mt-3 aspect-[16/9] overflow-hidden bg-muted">
                    <img src={form.photo_couverture} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              {/* Photos */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-eyebrow">Photos du carnet</label>
                  <input ref={photoInput} type="file" accept="image/*" multiple className="hidden" onChange={onPhotoFile} />
                  <button type="button" onClick={() => photoInput.current?.click()} className="text-eyebrow text-primary link-underline-static" disabled={uploading} data-testid="real-photos-upload">
                    <Upload size={12} className="inline mr-1" /> Importer des photos
                  </button>
                </div>
                <div className="space-y-2">
                  {form.photos.map((p, i) => (
                    <div key={p.url || `photo-${i}`} className="flex items-center gap-3 border border-border p-2" data-testid={`real-photo-${i}`}>
                      <img src={p.url} alt="" className="w-14 h-14 object-cover" />
                      <input
                        placeholder="Légende"
                        value={p.legende || ""}
                        onChange={(e) => {
                          const photos = [...form.photos];
                          photos[i] = { ...photos[i], legende: e.target.value };
                          setForm({ ...form, photos });
                        }}
                        className="flex-1 bg-transparent border-b border-border py-1.5 text-[13px] outline-none"
                      />
                      <button type="button" onClick={() => setForm({ ...form, photos: form.photos.filter((_, idx) => idx !== i) })} className="text-muted-foreground hover:text-destructive">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Avant / Après — slider de comparaison */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <label className="text-eyebrow">Avant / Après</label>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Particulièrement convertissant pour les dossiers sinistres.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addAvantApresPair}
                    className="text-eyebrow text-primary link-underline-static"
                    data-testid="real-avantapres-add"
                  >
                    <Plus size={12} className="inline mr-1" /> Ajouter une paire
                  </button>
                </div>
                <div className="space-y-3">
                  {(form.avant_apres || []).map((pair, i) => (
                    <div key={`pair-${i}`} className="border border-border p-3" data-testid={`real-avantapres-${i}`}>
                      <div className="grid grid-cols-2 gap-3">
                        <AvantApresSlot
                          label="Avant"
                          url={pair.avant_url}
                          onPick={(e) => onAvantApresFile(e, i, "avant_url")}
                          testId={`real-avantapres-${i}-avant`}
                          uploading={uploading}
                        />
                        <AvantApresSlot
                          label="Après"
                          url={pair.apres_url}
                          onPick={(e) => onAvantApresFile(e, i, "apres_url")}
                          testId={`real-avantapres-${i}-apres`}
                          uploading={uploading}
                        />
                      </div>
                      <div className="flex items-center gap-3 mt-3">
                        <input
                          placeholder="Légende (optionnelle)"
                          value={pair.legende || ""}
                          onChange={(e) => setAvantApresLegende(i, e.target.value)}
                          className="flex-1 bg-transparent border-b border-border py-1.5 text-[13px] outline-none"
                          data-testid={`real-avantapres-${i}-legende`}
                        />
                        <button
                          type="button"
                          onClick={() => removeAvantApresPair(i)}
                          className="text-muted-foreground hover:text-destructive"
                          aria-label="Supprimer la paire"
                          data-testid={`real-avantapres-${i}-remove`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-5 pt-2">
                <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <input type="checkbox" checked={form.est_phare} onChange={(e) => setForm({ ...form, est_phare: e.target.checked })} data-testid="real-phare" />
                  Projet phare
                </label>
                <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <input type="checkbox" checked={form.publiee} onChange={(e) => setForm({ ...form, publiee: e.target.checked })} data-testid="real-publiee" />
                  Publiée sur le site
                </label>
                <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <input type="checkbox" checked={form.coordination_expert} onChange={(e) => setForm({ ...form, coordination_expert: e.target.checked })} data-testid="real-coord" />
                  Coordination expert
                </label>
              </div>
            </div>
            <button type="submit" className={`btn-terra w-full justify-center mt-8 ${dirty ? "ring-2 ring-primary/40 ring-offset-2 ring-offset-background" : ""}`} disabled={uploading} data-testid="real-save">
              {dirty ? "Enregistrer les modifications" : (editing ? "Mettre à jour" : "Créer la réalisation")}
            </button>
            {dirty && (
              <p className="text-[12px] text-primary mt-3 text-center">
                Modifications non enregistrées — clique pour publier sur le site.
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="text-eyebrow block mb-2">{label}</label>
      <input
        type={type}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[14.5px] outline-none transition-colors"
        data-testid={testId}
      />
    </div>
  );
}

function AvantApresSlot({ label, url, onPick, testId, uploading }) {
  const inputRef = useRef();
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.22em] uppercase font-medium text-foreground/70">
          {label}
        </span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-[11px] text-primary link-underline-static"
          disabled={uploading}
          data-testid={`${testId}-pick`}
        >
          <Upload size={11} className="inline mr-1" /> Choisir
        </button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
      </div>
      <div className="aspect-[4/3] bg-muted overflow-hidden border border-border">
        {url ? (
          <img src={url} alt={`${label} preview`} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[11px] text-muted-foreground">
            Aucune image
          </div>
        )}
      </div>
    </div>
  );
}

